// utils/locationUtils.js or wherever your utils are located

/**
 * Comprehensive user location detection utility
 * Returns country code and other location information
 */

// Timezone to country mapping
const TIMEZONE_TO_COUNTRY = {
  // North America
  "America/New_York": "US",
  "America/Chicago": "US",
  "America/Denver": "US",
  "America/Los_Angeles": "US",
  "America/Phoenix": "US",
  "America/Toronto": "CA",
  "America/Vancouver": "CA",
  "America/Mexico_City": "MX",

  // Europe
  "Europe/London": "GB",
  "Europe/Dublin": "IE",
  "Europe/Paris": "FR",
  "Europe/Berlin": "DE",
  "Europe/Rome": "IT",
  "Europe/Madrid": "ES",
  "Europe/Amsterdam": "NL",
  "Europe/Stockholm": "SE",
  "Europe/Moscow": "RU",

  // Asia
  "Asia/Karachi": "PK",
  "Asia/Lahore": "PK",
  "Asia/Dubai": "AE",
  "Asia/Riyadh": "SA",
  "Asia/Kolkata": "IN",
  "Asia/Mumbai": "IN",
  "Asia/Delhi": "IN",
  "Asia/Dhaka": "BD",
  "Asia/Shanghai": "CN",
  "Asia/Tokyo": "JP",
  "Asia/Seoul": "KR",
  "Asia/Singapore": "SG",
  "Asia/Bangkok": "TH",
  "Asia/Jakarta": "ID",

  // Middle East
  "Asia/Tehran": "IR",
  "Asia/Kuwait": "KW",
  "Asia/Qatar": "QA",
  "Asia/Bahrain": "BH",

  // Australia & Oceania
  "Australia/Sydney": "AU",
  "Australia/Melbourne": "AU",
  "Australia/Perth": "AU",
  "Pacific/Auckland": "NZ",

  // Africa
  "Africa/Cairo": "EG",
  "Africa/Lagos": "NG",
  "Africa/Johannesburg": "ZA",
  "Africa/Casablanca": "MA",
};

// Language to country mapping
const LANGUAGE_TO_COUNTRY = {
  "en-US": "US",
  "en-GB": "GB",
  "en-CA": "CA",
  "en-AU": "AU",
  "ur-PK": "PK",
  "hi-IN": "IN",
  "ar-SA": "SA",
  "ar-AE": "AE",
  "fr-FR": "FR",
  "de-DE": "DE",
  "es-ES": "ES",
  "it-IT": "IT",
  "pt-BR": "BR",
  "ru-RU": "RU",
  "ja-JP": "JP",
  "ko-KR": "KR",
  "zh-CN": "CN",
  "th-TH": "TH",
  "id-ID": "ID",
};

// Country code to country name mapping
const COUNTRY_NAMES = {
  // Asia
  PK: "Pakistan",
  IN: "India",
  BD: "Bangladesh",
  LK: "Sri Lanka",
  AF: "Afghanistan",
  IR: "Iran",
  IQ: "Iraq",
  SA: "Saudi Arabia",
  AE: "United Arab Emirates",
  QA: "Qatar",
  KW: "Kuwait",
  BH: "Bahrain",
  OM: "Oman",
  YE: "Yemen",
  JO: "Jordan",
  LB: "Lebanon",
  SY: "Syria",
  TR: "Turkey",
  IL: "Israel",
  PS: "Palestine",
  CN: "China",
  JP: "Japan",
  KR: "South Korea",
  KP: "North Korea",
  MN: "Mongolia",
  TH: "Thailand",
  VN: "Vietnam",
  LA: "Laos",
  KH: "Cambodia",
  MM: "Myanmar",
  MY: "Malaysia",
  SG: "Singapore",
  ID: "Indonesia",
  BN: "Brunei",
  PH: "Philippines",
  TW: "Taiwan",
  HK: "Hong Kong",
  MO: "Macau",
  KZ: "Kazakhstan",
  UZ: "Uzbekistan",
  TM: "Turkmenistan",
  KG: "Kyrgyzstan",
  TJ: "Tajikistan",
  NP: "Nepal",
  BT: "Bhutan",
  MV: "Maldives",

  // Europe
  GB: "United Kingdom",
  IE: "Ireland",
  FR: "France",
  ES: "Spain",
  PT: "Portugal",
  IT: "Italy",
  DE: "Germany",
  AT: "Austria",
  CH: "Switzerland",
  LI: "Liechtenstein",
  NL: "Netherlands",
  BE: "Belgium",
  LU: "Luxembourg",
  DK: "Denmark",
  NO: "Norway",
  SE: "Sweden",
  FI: "Finland",
  IS: "Iceland",
  PL: "Poland",
  CZ: "Czech Republic",
  SK: "Slovakia",
  HU: "Hungary",
  SI: "Slovenia",
  HR: "Croatia",
  BA: "Bosnia and Herzegovina",
  RS: "Serbia",
  ME: "Montenegro",
  MK: "North Macedonia",
  AL: "Albania",
  BG: "Bulgaria",
  RO: "Romania",
  MD: "Moldova",
  UA: "Ukraine",
  BY: "Belarus",
  RU: "Russia",
  LT: "Lithuania",
  LV: "Latvia",
  EE: "Estonia",
  GR: "Greece",
  CY: "Cyprus",
  MT: "Malta",

  // North America
  US: "United States",
  CA: "Canada",
  MX: "Mexico",
  GT: "Guatemala",
  BZ: "Belize",
  SV: "El Salvador",
  HN: "Honduras",
  NI: "Nicaragua",
  CR: "Costa Rica",
  PA: "Panama",

  // Caribbean
  CU: "Cuba",
  JM: "Jamaica",
  HT: "Haiti",
  DO: "Dominican Republic",
  PR: "Puerto Rico",
  TT: "Trinidad and Tobago",
  BB: "Barbados",

  // South America
  BR: "Brazil",
  AR: "Argentina",
  CL: "Chile",
  PE: "Peru",
  BO: "Bolivia",
  PY: "Paraguay",
  UY: "Uruguay",
  CO: "Colombia",
  VE: "Venezuela",
  GY: "Guyana",
  SR: "Suriname",
  EC: "Ecuador",

  // Africa
  EG: "Egypt",
  LY: "Libya",
  TN: "Tunisia",
  DZ: "Algeria",
  MA: "Morocco",
  SD: "Sudan",
  SS: "South Sudan",
  ET: "Ethiopia",
  ER: "Eritrea",
  SO: "Somalia",
  DJ: "Djibouti",
  KE: "Kenya",
  UG: "Uganda",
  TZ: "Tanzania",
  RW: "Rwanda",
  BI: "Burundi",
  MG: "Madagascar",
  MU: "Mauritius",
  SC: "Seychelles",
  ZA: "South Africa",
  NA: "Namibia",
  BW: "Botswana",
  ZM: "Zambia",
  ZW: "Zimbabwe",
  MW: "Malawi",
  MZ: "Mozambique",
  SZ: "Eswatini",
  LS: "Lesotho",
  AO: "Angola",
  CD: "Democratic Republic of Congo",
  CG: "Republic of Congo",
  CF: "Central African Republic",
  CM: "Cameroon",
  TD: "Chad",
  NE: "Niger",
  NG: "Nigeria",
  BJ: "Benin",
  TG: "Togo",
  GH: "Ghana",
  CI: "Ivory Coast",
  LR: "Liberia",
  SL: "Sierra Leone",
  GN: "Guinea",
  GW: "Guinea-Bissau",
  SN: "Senegal",
  GM: "Gambia",
  ML: "Mali",
  BF: "Burkina Faso",
  MR: "Mauritania",

  // Oceania
  AU: "Australia",
  NZ: "New Zealand",
  PG: "Papua New Guinea",
  FJ: "Fiji",
  SB: "Solomon Islands",
  NC: "New Caledonia",
  PF: "French Polynesia",
  WS: "Samoa",
  TO: "Tonga",
  VU: "Vanuatu",
  KI: "Kiribati",
  TV: "Tuvalu",
  NR: "Nauru",
  PW: "Palau",
  FM: "Micronesia",
  MH: "Marshall Islands",
};

/**
 * Get country from browser timezone
 */
function getCountryFromTimezone() {
  try {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return TIMEZONE_TO_COUNTRY[timeZone] || null;
  } catch (error) {
    console.error("Error getting country from timezone:", error);
    return null;
  }
}

/**
 * Get country from browser language
 */
function getCountryFromLanguage() {
  try {
    const languages = navigator.languages || [
      navigator.language || navigator.userLanguage,
    ];

    for (let language of languages) {
      const country = LANGUAGE_TO_COUNTRY[language];
      if (country) return country;

      // Try with just the country part (e.g., 'en-US' -> 'US')
      const countryCode = language.split("-")[1];
      if (countryCode && countryCode.length === 2) {
        return countryCode.toUpperCase();
      }
    }
    return null;
  } catch (error) {
    console.error("Error getting country from language:", error);
    return null;
  }
}

/**
 * Get country from IP using free API
 */
async function getCountryFromIP() {
  const APIs = [
    "https://ipapi.co/json/",
    "https://api.ipify.org?format=json", // Fallback
    "https://ipinfo.io/json",
  ];

  for (let apiUrl of APIs) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      const response = await fetch(apiUrl, {
        signal: controller.signal,
        method: "GET",
      });

      clearTimeout(timeoutId);

      if (response.ok) {
        const data = await response.json();

        // Different APIs return country in different fields
        const country = data.country_code || data.country || data.countryCode;
        if (country) {
          return country.toUpperCase();
        }
      }
    } catch (error) {
      console.warn(`API ${apiUrl} failed:`, error.message);
      continue;
    }
  }

  return null;
}

/**
 * Get country name from country code
 */
function getCountryName(countryCode) {
  if (!countryCode || countryCode === "Unknown") {
    return "Unknown";
  }
  return COUNTRY_NAMES[countryCode.toUpperCase()] || countryCode;
}

/**
 * Main function to detect user location
 * Returns comprehensive location information
 */
export async function detectUserLocation() {
  const result = {
    countryCode: "Unknown",
    country: "Unknown",
    countrySource: "fallback",
    timezone: null,
    language: null,
    timestamp: new Date().toISOString(),
  };

  try {
    // Get timezone info
    result.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // Get language info
    result.language = navigator.language || navigator.userLanguage;

    // Method 1: Try timezone detection (fastest)
    const countryFromTZ = getCountryFromTimezone();
    if (countryFromTZ) {
      result.countryCode = countryFromTZ;
      result.country = getCountryName(countryFromTZ);
      result.countrySource = "timezone";
      return result;
    }

    // Method 2: Try language detection
    const countryFromLang = getCountryFromLanguage();
    if (countryFromLang) {
      result.countryCode = countryFromLang;
      result.country = getCountryName(countryFromLang);
      result.countrySource = "language";
      return result;
    }

    // Method 3: Try IP detection (requires internet)
    const countryFromIP = await getCountryFromIP();
    if (countryFromIP) {
      result.countryCode = countryFromIP;
      result.country = getCountryName(countryFromIP);
      result.countrySource = "ip";
      return result;
    }
  } catch (error) {
    console.error("Error in detectUserLocation:", error);
  }

  return result;
}

/**
 * Simplified function that just returns country code
 * For backward compatibility with existing code
 */
export async function getUserCountry() {
  const location = await detectUserLocation();
  return location.countryCode;
}

/**
 * Get full country name
 */
export async function getUserCountryName() {
  const location = await detectUserLocation();
  return location.country;
}

/**
 * Synchronous version (less accurate but faster)
 * Uses only browser APIs, no network requests
 */
export function detectUserLocationSync() {
  const result = {
    countryCode: "Unknown",
    country: "Unknown",
    countrySource: "fallback",
    timezone: null,
    language: null,
  };

  try {
    result.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    result.language = navigator.language || navigator.userLanguage;

    const countryFromTZ = getCountryFromTimezone();
    if (countryFromTZ) {
      result.countryCode = countryFromTZ;
      result.country = getCountryName(countryFromTZ);
      result.countrySource = "timezone";
      return result;
    }

    const countryFromLang = getCountryFromLanguage();
    if (countryFromLang) {
      result.countryCode = countryFromLang;
      result.country = getCountryName(countryFromLang);
      result.countrySource = "language";
      return result;
    }
  } catch (error) {
    console.error("Error in detectUserLocationSync:", error);
  }

  return result;
}

/**
 * Helper function to get country name from code
 */
export function getCountryNameFromCode(countryCode) {
  return getCountryName(countryCode);
}

// Default export for backward compatibility
export default detectUserLocation;
